/**
 * Logout → new guest cart (TWS-1076).
 *
 * The store runs New Customer Accounts, so logout is not always a click on the theme
 * link (hosted account UI, expired session, logout in another tab). Two paths:
 *   1. Click on a logout link — expire the cookie and navigate in the same tick, with no
 *      network call, so nothing can persist a fresh cart token while still signed in.
 *   2. Any guest page load that still carries the owner marker written while logged in.
 *      This is where verification and any fallback happens: after logout neither
 *      cart-token-sync nor shared-cart-sync can write custom.basketid.
 *
 * Why not /cart/clear.js?
 *   The Ajax cart token is often === custom.basketid, so emptying lines would empty the
 *   shared Storefront cart and wipe mobile. Expiring the browser `cart` cookie hands the
 *   next visitor a new empty cart while basketid keeps its lines. Zeroing lines stays a
 *   last resort for when the cookie cannot be detached: a guest must never inherit the
 *   previous customer's bag.
 */
(function () {
  var PROXY_CLEAR_URL = '/apps/cart-sync/api/v1/browser-cart/clear';
  var PROXY_TIMEOUT_MS = 1200;
  var OWNER_KEY = 'tjc_cart_owner';
  var RELOAD_GUARD_KEY = 'tjc_cart_logout_reloaded';
  var LOGOUT_LINK_SELECTOR =
    'a[data-clear-cart-on-logout], a[href*="/account/logout"], a[href*="/customer_authentication/logout"]';

  /**
   * @param {string} label
   * @param {Record<string, unknown>} [data]
   */
  function logDebug(label, data) {
    console.debug('[logout-clear-cart] ' + label, data || {});
  }

  /** @returns {string} raw `cart` cookie value, empty when absent */
  function readCartCookie() {
    var match = /(?:^|;\s*)cart=([^;]*)/.exec(document.cookie);
    return match && match[1] ? match[1] : '';
  }

  /**
   * @param {Storage | null} storage
   * @param {string} key
   * @returns {string}
   */
  function readStored(storage, key) {
    try {
      return (storage && storage.getItem(key)) || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * @param {Storage | null} storage
   * @param {string} key
   * @param {string} value
   */
  function writeStored(storage, key, value) {
    try {
      if (storage) storage.setItem(key, value);
    } catch (e) {
      /* private mode / quota */
    }
  }

  /**
   * @param {Storage | null} storage
   * @param {string} key
   */
  function removeStored(storage, key) {
    try {
      if (storage) storage.removeItem(key);
    } catch (e) {
      /* private mode */
    }
  }

  /** @returns {Storage | null} */
  function localStore() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  /** @returns {Storage | null} */
  function sessionStore() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  /** @returns {string} logged-in customer id, empty for guests */
  function readCustomerId() {
    var el = document.getElementById('logout-clear-cart-config');
    if (!el || !el.textContent) return '';
    try {
      var parsed = JSON.parse(el.textContent);
      return parsed && parsed.customerId ? String(parsed.customerId) : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Shopify sets `cart` host-only with path=/, so the no-domain pass is the one that
   * matches. The domain passes cover apex/subdomain variants on other environments.
   * @param {string} name
   */
  function expireCookie(name) {
    var domains = [''];
    try {
      var host = window.location.hostname;
      if (host) {
        domains.push(host);
        var parts = host.split('.');
        if (parts.length >= 2) {
          domains.push('.' + parts.slice(-2).join('.'));
        }
        if (parts.length >= 3) {
          domains.push('.' + parts.slice(-3).join('.'));
        }
      }
    } catch (e) {
      /* ignore */
    }

    for (var i = 0; i < domains.length; i++) {
      var domainPart = domains[i] ? '; domain=' + domains[i] : '';
      document.cookie = name + '=; Max-Age=0; path=/' + domainPart + '; SameSite=Lax';
      document.cookie = name + '=; Max-Age=0; path=/' + domainPart + '; Secure; SameSite=Lax';
      document.cookie = name + '=; Max-Age=0; path=/' + domainPart + '; SameSite=None; Secure';
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domainPart;
    }
  }

  function clearBrowserCartCookiesLocal() {
    expireCookie('cart');
    expireCookie('cart_ts');
    expireCookie('cart_sig');
    expireCookie('cart_currency');

    var session = sessionStore();
    removeStored(session, 'tjc_shared_cart_restored');
    removeStored(session, 'tjc_shared_cart_adopt_attempts');
    removeStored(session, 'tjc_shared_cart_prefer_cart_page');
    removeStored(session, 'tjc_shared_cart_merge_guard');
  }

  /**
   * Cart-Sync app proxy can expire the cookie server-side; theme still expires locally.
   * @returns {Promise<void>}
   */
  function requestProxyClear() {
    /** @type {AbortController | null} */
    var controller = null;
    try {
      controller = new AbortController();
    } catch (e) {
      controller = null;
    }
    var timer = controller
      ? setTimeout(function () {
          if (controller) controller.abort();
        }, PROXY_TIMEOUT_MS)
      : 0;

    /** @type {RequestInit} */
    var options = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: '{}',
    };
    if (controller) options.signal = controller.signal;

    return fetch(PROXY_CLEAR_URL, options)
      .catch(function () {
        /* local expire still runs */
      })
      .then(function () {
        if (timer) clearTimeout(timer);
      });
  }

  /** @returns {Promise<Record<string, any> | null>} */
  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * @param {Record<string, any> | null} cart
   * @returns {number}
   */
  function countItems(cart) {
    var count = cart ? Number(cart.item_count) : 0;
    return isFinite(count) ? count : 0;
  }

  /**
   * Last resort when the cookie could not be detached — privacy beats keeping the
   * shared basketid lines, otherwise the next guest inherits this bag.
   * @param {Record<string, any> | null} cart
   * @returns {Promise<void>}
   */
  function zeroCartLines(cart) {
    var items = cart && cart.items ? cart.items : [];
    /** @type {Record<string, number>} */
    var updates = {};
    for (var i = 0; i < items.length; i++) {
      var id = String(items[i].variant_id || items[i].id || '');
      if (id) updates[id] = 0;
    }
    if (Object.keys(updates).length === 0) return Promise.resolve();

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function () {
        /* verified by the caller */
      })
      .catch(function () {
        /* nothing else to try */
      });
  }

  /**
   * Guest-only: reading /cart.js materialises a new cart, and while a customer is still
   * signed in cart-token-sync + shared-cart-sync would persist that new token to
   * custom.basketid. After logout neither script is loaded, so this is safe here.
   * @returns {Promise<number>} item count left on this browser
   */
  function startNewGuestCart() {
    clearBrowserCartCookiesLocal();

    // Only involve the app proxy when the cookie resisted a local expiry.
    var detached = readCartCookie() === '';
    var ensureDetached = detached
      ? Promise.resolve()
      : requestProxyClear().then(clearBrowserCartCookiesLocal);

    return ensureDetached
      .then(fetchCart)
      .then(function (cart) {
        var count = countItems(cart);
        logDebug('after cookie expiry', {
          cartCookie: readCartCookie(),
          token: cart ? cart.token : null,
          itemCount: count,
        });
        if (count === 0) return 0;
        return zeroCartLines(cart)
          .then(fetchCart)
          .then(countItems);
      });
  }

  /** @param {number} count */
  function updateCartBubble(count) {
    document.querySelectorAll('.cart-bubble__text-count').forEach(function (el) {
      el.textContent = count < 100 ? String(count) : '';
      el.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('.cart-bubble').forEach(function (bubble) {
      bubble.classList.toggle('visually-hidden', count === 0);
      bubble.classList.remove('hidden');
    });
    document.querySelectorAll('cart-icon').forEach(function (icon) {
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
  }

  /**
   * Shadow roots retarget `event.target` to their host, so walk the composed path.
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  function findLogoutLink(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node instanceof HTMLAnchorElement && node.matches(LOGOUT_LINK_SELECTOR)) {
        return node;
      }
    }
    var target = event.target instanceof Element ? event.target : null;
    var link = target && target.closest(LOGOUT_LINK_SELECTOR);
    return link instanceof HTMLAnchorElement ? link : null;
  }

  document.addEventListener(
    'click',
    function (event) {
      var link = findLogoutLink(event);
      if (!link) return;

      event.preventDefault();
      event.stopPropagation();

      var logoutUrl = link.href;
      removeStored(sessionStore(), RELOAD_GUARD_KEY);

      // Detach the cookie and leave in the same tick. No fetch of any kind: the customer
      // is still signed in, so any read of /cart.js would materialise a fresh cart that
      // cart-token-sync writes back via /cart/update.js, which shared-cart-sync then
      // persists to custom.basketid — wiping the shared (mobile) cart. Lines that survive
      // the cookie detach are handled by the guest path on the next page load.
      clearBrowserCartCookiesLocal();
      logDebug('logout click', {
        cartCookie: readCartCookie(),
        detached: readCartCookie() === '',
        logoutUrl: logoutUrl,
      });

      window.location.href = logoutUrl;
    },
    true
  );

  function init() {
    var customerId = readCustomerId();
    var local = localStore();

    if (customerId) {
      writeStored(local, OWNER_KEY, customerId);
      removeStored(sessionStore(), RELOAD_GUARD_KEY);
      return;
    }

    if (!readStored(local, OWNER_KEY)) return;
    // Guest still holding the cart of the customer who was signed in on this browser.
    removeStored(local, OWNER_KEY);
    if (window.Shopify && window.Shopify.designMode) return;

    fetchCart().then(function (before) {
      var hadItems = countItems(before) > 0;
      logDebug('guest page load with customer cart', {
        cartCookie: readCartCookie(),
        token: before ? before.token : null,
        itemCount: countItems(before),
      });
      startNewGuestCart().then(function (count) {
        updateCartBubble(count);
        // Cart page / drawer are server rendered, so re-render once when items were shown.
        var session = sessionStore();
        if (hadItems && count === 0 && !readStored(session, RELOAD_GUARD_KEY)) {
          writeStored(session, RELOAD_GUARD_KEY, '1');
          window.location.reload();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
